import styled from 'styled-components';
import StepConnector, {
  stepConnectorClasses
} from '@mui/material/StepConnector';
import StepLabel, { stepLabelClasses } from '@mui/material/StepLabel';
import { typographyClasses } from '@mui/material/Typography';
import { flexCenter } from '../../utils/style';
import { StyledButton } from '../chat/Button/Styled';
import Label from '../chat/Label';
import { StyledCheckbox } from '../chat/Checkbox/Styled';
import { StyledHeading } from '../chat/Heading/Styled';
import { StyledInput } from '../chat/Input/Styled';
import { Stepper, StepperProps, stepperClasses } from '@mui/material';
import { StyledFormField } from '../chat/FormField/Styled';
import { ErrorText } from '../../styles/chime-react/CommonStyles';

export const CustomStepper = styled((props: StepperProps) => (
  <Stepper alternativeLabel connector={<CustomStepConnector />} {...props} />
))(({ theme }) => ({
  [`&.${stepperClasses.root}`]: {
    width: 250,
    margin: 'auto'
  },
  [`${theme.mediaQueries.min.md}`]: {
    [`&.${stepperClasses.root}`]: {
      width: 510
    }
  }
}));

export const CustomStepConnector = styled(StepConnector)((props) => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: 25,
    left: '-50%',
    right: '50%'
  },
  [`&.${stepConnectorClasses.active}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      borderColor: props.theme.colors.primary.main
    }
  },
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      borderColor: props.theme.colors.primary.main
    }
  },
  [`& .${stepConnectorClasses.line}`]: {
    borderColor: props.theme.colors.greys.grey20,
    borderTopWidth: 4
  }
}));

export const CustomStepIconRoot = styled('div')<{
  ownerState: { completed?: boolean; active?: boolean };
}>(({ theme, ownerState }) => ({
  height: 33,
  width: 12,
  paddingTop: 21,
  position: 'relative',
  zIndex: 1,
  '& .CustomStepIcon-flagIcon': {
    visibility: 'hidden',
    color: theme.colors.primary.main,
    position: 'absolute',
    top: 0,
    left: 4,
    zIndex: 2,
    ...((ownerState.active || ownerState.completed) && {
      visibility: 'visible'
    })
  },
  '& .CustomStepIcon-circle': {
    width: 12,
    height: 12,
    borderRadius: '50%',
    backgroundColor: theme.colors.greys.grey20,
    position: 'absolute',
    bottom: 0,
    left: 0,
    zIndex: 1,
    ...(ownerState.active && {
      backgroundColor: theme.colors.info.primary
    }),
    ...(ownerState.completed && {
      width: 8,
      height: 8,
      backgroundColor: theme.colors.primary.main,
      bottom: 2,
      left: 2
    })
  }
}));

export const CustomStepLabel = styled(StepLabel)<{ $smallText?: boolean }>(
  (props) => ({
    [`&.${stepLabelClasses.alternativeLabel}`]: {
      color: props.theme.colors.greys.grey20,

      [`& .${stepLabelClasses.labelContainer}`]: {
        color: 'currentColor'
      },

      [`& .${stepLabelClasses.label}`]: {
        marginTop: 12,
        color: 'currentColor',
        fontSize:
          props.theme.fontSizes[props.$smallText ? 'h6' : 'h5'].fontSize,
        lineHeight: 1,
        fontWeight: 'bold',
        fontFamily: 'inherit',
        whiteSpace: 'nowrap',
        height: 24,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',

        [`&.${stepLabelClasses.completed}`]: {
          color: props.theme.colors.primary.main,

          [`& + .${typographyClasses.caption}`]: {
            color: props.theme.colors.primary.main
          }
        },
        [`&.${stepLabelClasses.active}`]: {
          color: props.theme.colors.primary.main,

          [`& + .${typographyClasses.caption}`]: {
            color: props.theme.colors.primary.main
          }
        }
      },

      [`& .${typographyClasses.caption}`]: {
        fontFamily: 'inherit'
      }
    }
  })
);

export const StyledStepExplanation = styled.div`
  display: flex;
  justify-content: space-between;
  position: relative;
  margin-top: 1.5rem;

  & {
    .explanation-image {
      margin-left: 0.75rem;
    }
  }

  ${({ theme }) => theme.mediaQueries.min.md} {
    align-items: center;
    margin-top: 2.5rem;
    min-height: 11.875rem;
    font-size: ${(props) => props.theme.fontSizes.text.fontSize};

    & {
      .explanation-image {
        position: absolute;
        top: 50%;
        right: 0;
        margin-left: 0;
        transform: translateY(-50%);
      }
    }

    &:first-child {
      margin-top: 0;

      .explanation-image {
        position: static;
      }
    }
  }
`;

export const StyledExplanationBox = styled.div`
  padding: 0.75rem 0.5rem;
  background-color: ${(props) => props.theme.colors.info.primary};
  border-radius: 0.5rem 0 0.5rem 0.5rem;
  box-shadow: ${(props) => props.theme.chatBubble.boxShadow};
  font-size: ${(props) => props.theme.fontSizes.text.fontSize};
  line-height: ${(props) => props.theme.fontSizes.text.lineHeight};
  font-weight: bold;
  color: ${(props) => props.theme.colors.primary.main};
  ${flexCenter}
  white-space: pre-wrap;

  ${({ theme }) => theme.mediaQueries.min.md} {
    width: 29.0625rem;
    padding: 1.5rem;
    font-size: ${(props) => props.theme.fontSizes.h5.fontSize};
    line-height: ${(props) => props.theme.fontSizes.h5.lineHeight};
  }
`;

export const StyledStepContent = styled.div`
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid ${(props) => props.theme.colors.greys.grey20};

  & {
    ${StyledInput} {
      font-size: ${(props) => props.theme.fontSizes.h6.fontSize};
    }

    ${StyledFormField} .ch-help-text, ${ErrorText} {
      font-size: ${(props) => props.theme.fontSizes.text.fontSize};
      line-height: ${(props) => props.theme.fontSizes.text.lineHeight};
    }

    .input-wrap {
      display: grid;
      gap: 1rem;

      ${StyledButton} {
        justify-self: flex-start;
        font-size: ${(props) => props.theme.fontSizes.h5.fontSize};
        line-height: ${(props) => props.theme.fontSizes.h5.lineHeight};
        padding: 0.5rem 1.5rem;
      }
    }
  }

  ${({ theme }) => theme.mediaQueries.min.md} {
    margin-top: 2.5rem;
    padding-top: 2rem;

    & {
      .input-wrap {
        grid-template-columns: 1fr auto;

        ${StyledButton} {
          font-size: ${(props) => props.theme.fontSizes.h4.fontSize};
          line-height: ${(props) => props.theme.fontSizes.h4.lineHeight};
          padding: 0.5rem 1rem;
        }
      }
    }
  }
`;

export const StyledLargeLabel = styled(Label)`
  font-size: ${(props) => props.theme.fontSizes.h4.fontSize};
  line-height: ${(props) => props.theme.fontSizes.h4.lineHeight};
  margin-bottom: 1.25rem;

  ${StyledCheckbox} {
    margin-right: 1rem;
  }
`;

export const StyledCloseLinkWrap = styled.div`
  margin-top: 1.5rem;
  ${flexCenter}
`;

export const StyledCloseLink = styled.p`
  font-size: ${(props) => props.theme.fontSizes.text.fontSize};
  line-height: ${(props) => props.theme.fontSizes.text.lineHeight};
  color: ${(props) => props.theme.colors.primary.main};
  text-decoration: underline;
  cursor: pointer;
`;

export const StyledSampleDisplayContent = styled.div`
  font-size: ${(props) => props.theme.fontSizes.small.fontSize};
  line-height: ${(props) => props.theme.fontSizes.small.lineHeight};
  color: ${(props) => props.theme.colors.greys.grey50};
  text-align: center;

  & {
    .sample-display-image {
      margin-bottom: 0.5rem;
      border-radius: ${(props) => props.theme.radii.circle};
    }
  }

  ${({ theme }) => theme.mediaQueries.min.md} {
    margin-top: 2.25rem;
  }
`;

export const StyledSampleDisplayWrapper = styled.div`
  & + & {
    margin-top: 2rem;
  }

  ${({ theme }) => theme.mediaQueries.min.md} {
    display: grid;
    grid-template-columns: 1fr auto;
    column-gap: 1.5rem;
  }
`;

export const StyledNoteText = styled.div`
  margin-top: 0.5rem;
  font-size: ${(props) => props.theme.fontSizes.text.fontSize};
  line-height: ${(props) => props.theme.fontSizes.text.lineHeight};
  color: ${(props) => props.theme.colors.greys.grey50};
`;

export const StyledStepButtonWrap = styled.div`
  margin-top: 2rem;
  display: grid;
  align-items: center;
  justify-items: center;

  ${StyledButton} {
    font-size: ${(props) => props.theme.fontSizes.h5.fontSize};
    line-height: ${(props) => props.theme.fontSizes.h5.lineHeight};
    padding: 0.75rem 2rem;
    min-width: 14.25rem;
  }

  ${({ theme }) => theme.mediaQueries.min.md} {
    margin-top: 2.5rem;
    align-items: unset;
    justify-items: unset;
    place-content: center;

    ${StyledHeading} {
      place-self: center;
    }

    ${StyledButton} {
      font-size: ${(props) => props.theme.fontSizes.h3.fontSize};
      line-height: ${(props) => props.theme.fontSizes.h3.lineHeight};
      padding: 1rem 2.5rem;
      width: 100%;
      min-width: 12rem;
      min-height: 4rem;
    }
  }
`;

export const StyledContentBox = styled.div`
  background-color: ${(props) => props.theme.colors.greys.white};
  border-radius: ${(props) => props.theme.radii.large};
  box-shadow: ${(props) => props.theme.shadows.medium};
  padding: 1.25rem 1rem;
  font-size: ${(props) => props.theme.fontSizes.small.fontSize};
  line-height: ${(props) => props.theme.fontSizes.text.lineHeight};

  &.end {
    ${StyledStepExplanation} {
      & {
        .explanation-image {
          margin-top: -21px;
        }
      }
    }
  }

  ${({ theme }) => theme.mediaQueries.min.md} {
    padding: 2.5rem 5rem;
    font-size: ${(props) => props.theme.fontSizes.text.fontSize};

    &.end {
      ${StyledStepExplanation} {
        & {
          .explanation-image {
            margin-top: 0;
          }
        }
      }
    }
  }
`;

export const StyledInquiryWrap = styled.div`
  & {
    .inquiry-explanation-image {
      margin-top: -110px;
      width: 100%;
      height: auto;
    }
  }
`;
